/**
 * Full ERP navigation tree.
 * Mỗi module có path duy nhất dùng làm route (hoặc query param cho tab con).
 * Sidebar render đệ quy từ cây này.
 */
import {
  Article,
  Autorenew,
  ShoppingCart,
  Store,
  ShowChart,
  Science,
  Person,
  AccountBalance,
  Analytics,
  Assignment,
  Assessment,
  ContentPasteSearch,
  Anchor,
  CurrencyExchange,
  Description,
  Edit,
  EventNote,
  FactCheck,
  Gavel,
  Inventory,
  Link,
  LocalShipping,
  ManageAccounts,
  MoveToInbox,
  Paid,
  Payments,
  Receipt,
  ReceiptLong,
  RequestQuote,
  Rule,
  Scale,
  Security,
  Summarize,
  SwapHoriz,
} from '@mui/icons-material'
import type { SvgIconComponent } from '@mui/icons-material'
// Icon top-level copy từ mẫu design plan/ERP CTRM Home.dc.html (stroke style).
import {
  NavFactoryIcon,
  NavFuturesIcon,
  NavHomeIcon,
  NavMasterDataIcon,
  NavMatchingIcon,
  NavPurchaseContractIcon,
  NavQualityIcon,
  NavReportIcon,
  NavSaleContractIcon,
  NavSettingsIcon,
  NavWarehouseIcon,
} from './navIcons'

export interface NavItem {
  key: string
  label: string
  path: string
  icon?: SvgIconComponent
  /** Permission key từ settings/roles. Item bị ẩn nếu role có key này = NoGrant (2). */
  permKey?: string
  children?: NavItem[]
}

export const navigation: NavItem[] = [
  { key: 'home', label: 'Home', path: '/home', icon: NavHomeIcon },

  // ─── Report ────────────────────────────────────────
  {
    key: 'dashboard',
    label: 'Report',
    path: '/report',
    icon: NavReportIcon,
    permKey: 'position', // RT008 — Report/Commercial Position module
    children: [
      {
        // Nhóm các report (dashboard) hiện có.
        key: 'report-list',
        label: 'Dashboards',
        path: '/report/dashboards',
        icon: Assessment,
        children: [
          { key: 'report-pnl', label: 'P&L', path: '/report/dashboards/pnl', icon: Analytics },
          { key: 'report-futures-position', label: 'Futures Position', path: '/report/dashboards/futures-position', icon: ShowChart },
          { key: 'report-risk', label: 'Risk Management', path: '/report/dashboards/risk-management', icon: Security },
          { key: 'report-long-short', label: 'Long/Short', path: '/report/dashboards/long-short', icon: SwapHoriz },
          { key: 'report-inventory', label: 'Inventory', path: '/report/dashboards/inventory', icon: Inventory },
          { key: 'report-cashflows', label: 'Cash Flows', path: '/report/dashboards/cashflows', icon: Paid },
        ],
      },
      {
        // Các trang Edit Value (sẽ bổ sung dần). Thêm child mới vào đây + khai báo
        // route tương ứng trong App.tsx dưới `report/inputs/...`.
        key: 'report-inputs',
        label: 'Inputs',
        path: '/report/inputs',
        icon: Assignment,
        children: [
          { key: 'report-inputs-exposure', label: 'Exposure Limit', path: '/report/inputs/exposure', icon: Security },
          { key: 'report-inputs-cashflow', label: 'Cash Flows', path: '/report/inputs/cashflows', icon: Paid },
          { key: 'report-inputs-misa-mapping', label: 'Misa Mapping', path: '/report/inputs/misa-mapping', icon: Paid },
        ],
      },
    ],
  },

  // ─── Purchase Contract ───────────────────────────────────────────
  // 2026-07-02: tách Purchase/Sales thành 4 nav top-level theo flow hàng hoá
  // (Purchase Contract → Inbound Logistics → Sale Contract → Outbound Logistics).
  // URL path giữ nguyên — chỉ đổi cây sidebar.
  {
    key: 'purchase-contract',
    label: 'Purchase Contract',
    path: '/purchase/contract',
    icon: NavPurchaseContractIcon,
    permKey: 'purchase',
    children: [
      { key: 'pc-localP', label: 'Purchase', path: '/purchase/contract/list', icon: ShoppingCart },
      { key: 'pc-forwardP', label: 'Forward P', path: '/purchase/contract/forward-pu', icon: ShowChart },
      { key: 'pc-npeContract', label: 'NPE Contract', path: '/purchase/contract/npe-contract', icon: Description },
      { key: 'pc-amendment', label: 'Amendment', path: '/purchase/contract/amendment', icon: Edit },
      { key: 'pc-ptbfFixation', label: 'PTBF Fixation', path: '/purchase/contract/ptbf-fixation', icon: CurrencyExchange },
      { key: 'pc-npeFixation', label: 'NPE Fixation', path: '/purchase/contract/npe-fixation', icon: Paid },
      { key: 'pc-npeAllocation', label: 'NPE Allocation', path: '/purchase/contract/npe-allocation', icon: Rule },
      { key: 'pc-ptbfRolling', label: 'PTBF Rolling', path: '/purchase/contract/ptbf-rolling', icon: Autorenew },
      { key: 'pc-plans', label: 'Plans', path: '/purchase/contract/plans', icon: EventNote },
      { key: 'pc-references', label: 'References', path: '/purchase/contract/references', icon: Link },
    ],
  },

  // ─── Inbound Logistics (Purchase > Control cũ) ───────────────────
  {
    key: 'inbound-logistics',
    label: 'Inbound Logistics',
    path: '/purchase/control',
    icon: LocalShipping,
    permKey: 'procurement',
    children: [
      { key: 'procurement-purchase', label: 'Purchase', path: '/purchase/control/purchase-contract', icon: ShoppingCart },
      { key: 'procurement-contract-detail', label: 'Contract Detail', path: '/purchase/control/contract-detail', icon: Description },
      { key: 'procurement-pay-data', label: 'Pay Data', path: '/purchase/control/pay-data', icon: Payments },
      { key: 'procurement-invoice', label: 'Invoice', path: '/purchase/control/invoice', icon: ReceiptLong },
      { key: 'procurement-goods-allocation', label: 'Goods Allocation', path: '/purchase/control/goods-allocation', icon: Rule },
      { key: 'procurement-cvbb', label: 'Documents & Minutes', path: '/purchase/control/cvbb', icon: Article },
      { key: 'procurement-references', label: 'References', path: '/purchase/control/references', icon: Link },
    ],
  },

  // ─── Sale Contract ───────────────────────────────────────────────
  {
    key: 'sale-contract',
    label: 'Sales Contract',
    path: '/sales/contract',
    icon: NavSaleContractIcon,
    permKey: 'saleContract',
    children: [
      { key: 'sale-contracts', label: 'Sales', path: '/sales/contract/list', icon: Store },
      { key: 'consignment', label: 'Consignment', path: '/sales/contract/consignment', icon: Inventory },
      { key: 'sales-amendment', label: 'Amendment', path: '/sales/contract/amendment', icon: Edit },
      { key: 'sales-ptbf-fixation', label: 'PTBF Fixation', path: '/sales/contract/ptbf-fixation', icon: CurrencyExchange },
      { key: 'sales-contract-references', label: 'References', path: '/sales/contract/references', icon: Link },
    ],
  },

  // ─── Outbound Logistics (Sales > Control cũ) ─────────────────────
  {
    key: 'outbound-logistics',
    label: 'Outbound Logistics',
    path: '/sales/control',
    icon: Anchor,
    permKey: 'saleControl',
    children: [
      { key: 'sales-contract', label: 'Sales', path: '/sales/control/sales-contract', icon: Store },
      { key: 'sales-allocation', label: 'Allocation', path: '/sales/control/sales-allocation', icon: Rule },
      { key: 'delivery-order', label: 'Delivery Order', path: '/sales/control/delivery-order', icon: LocalShipping },
      { key: 'release-order', label: 'Release Order', path: '/sales/control/release-order', icon: MoveToInbox },
      { key: 'sales-invoice', label: 'Invoice', path: '/sales/control/invoice', icon: ReceiptLong },
      { key: 'claim', label: 'Claim', path: '/sales/control/claim', icon: Gavel },
      { key: 'credit-note', label: 'Credit Note', path: '/sales/control/credit-note', icon: Receipt },
      { key: 'debit-note', label: 'Debit Note', path: '/sales/control/debit-note', icon: RequestQuote },
      { key: 'moving', label: 'Moving', path: '/sales/control/moving', icon: SwapHoriz },
      { key: 'sales-references', label: 'References', path: '/sales/control/references', icon: Link },
    ],
  },

  // ─── Futures Control ─────────────────────────────────────────────
  {
    key: 'futures',
    label: 'Futures Control',
    path: '/futures',
    icon: NavFuturesIcon,
    permKey: 'futures',
    children: [
      { key: 'futures-summary', label: 'Summary', path: '/futures/summary', icon: Summarize },
      { key: 'margin-report', label: 'Margin Report', path: '/futures/margin-report', icon: Assessment },
      { key: 'market', label: 'Market', path: '/futures/market', icon: ShowChart },
      { key: 'futures-allocation', label: 'Allocation', path: '/futures/allocation', icon: Rule },
      { key: 'futures-orders', label: 'Futures', path: '/futures/orders', icon: ReceiptLong },
      { key: 'spreads-orders', label: 'Spreads', path: '/futures/spreads', icon: SwapHoriz },
      { key: 'pucontract', label: 'Purchase', path: '/futures/pucontract', icon: Paid },
      { key: 'local-s', label: 'Sales', path: '/futures/sacontract', icon: Paid },
      { key: 'bank-cash-in', label: 'Cash-in', path: '/futures/bank-cash-in', icon: AccountBalance },
      { key: 'bank-statement', label: 'Bank Statement', path: '/futures/bank-statement', icon: Description },
      { key: 'adjust-lot', label: 'Adjustment', path: '/futures/adjust-lot', icon: Scale },
      { key: 'bank-cost-change', label: 'Bank cost change', path: '/futures/bank-cost-change', icon: CurrencyExchange },
    ],
  },

  // ─── Factory ─────────────────────────────────────────────────────
  {
    key: 'factory',
    label: 'Factory',
    path: '/factory',
    icon: NavFactoryIcon,
    children: [
      // Path = factory_code (DB column tlc_factory.factory_overview). URL drop
      // prefix `/factory/` (2026-05-28) — `factoryCode` lên thẳng top-level
      // segment. Routes enumerated trong App.tsx.
      { key: 'baoloc', label: 'Bảo Lộc', path: '/baoloc', icon: NavFactoryIcon, permKey: 'factoryBl' },
      { key: 'nhontrach', label: 'Nhơn Trạch', path: '/nhontrach', icon: NavFactoryIcon, permKey: 'factoryNt' },
      { key: 'nguyencuong', label: 'An Phước', path: '/nguyencuong', icon: NavFactoryIcon, permKey: 'factoryNc' },
      { key: 'nongsan', label: 'Nông Sản', path: '/nongsan', icon: NavFactoryIcon, permKey: 'factoryNs' },
      { key: 'pacorini', label: 'Pacorini', path: '/pacorini', icon: NavFactoryIcon, permKey: 'factoryPaco' },
      // Virtual Warehouse v2 — kho mới kiểu factory (clone FactoryPage), code 'virtualwh'.
      // Reuse quyền Virtual Warehouse (RT006) trong lúc build thử.
      { key: 'virtualwh', label: 'Virtual Warehouse', path: '/virtualwh', icon: NavWarehouseIcon, permKey: 'virtualWarehouse' },
      { key: 'factory-control', label: 'Factory Control', path: '/factory/control', icon: Summarize, permKey: 'factory' },
    ],
  },

  // ─── Matching P - S ──────────────────────────────────────────────
  // Mirror Access form `frmMatchingPSMain` (NavigationControl0) — 4 nav button visible.
  {
    key: 'matching',
    label: 'Matching P - S',
    path: '/matching',
    icon: NavMatchingIcon,
    permKey: 'matchingPs',
    children: [
      { key: 'match-instruct', label: 'Match - Instruct', path: '/matching/match-instruct', icon: Rule },
      // Ẩn khỏi sidebar (route `/matching/stock-sales` vẫn hoạt động khi truy cập trực tiếp).
      // { key: 'stock-sales', label: 'Stock to Sales', path: '/matching/stock-sales', icon: SwapHoriz },
      { key: 's-contract', label: 'S Contract', path: '/matching/s-contract', icon: Store },
      { key: 'p-contract', label: 'P Contract', path: '/matching/p-contract', icon: ShoppingCart },
    ],
  },

  // ─── Quality ─────────────────────────────────────────────────────
  {
    key: 'quality',
    label: 'Quality',
    path: '/quality',
    icon: NavQualityIcon,
    permKey: 'quality',
    children: [
      { key: 'sampling', label: 'Overview', path: '/quality/overview', icon: Science },
      { key: 'sampling-data', label: 'Sample Data', path: '/quality/sample-data', icon: FactCheck },
      { key: 'nestle-sample', label: 'Nestle Sample', path: '/quality/nestle-sample', icon: LocalShipping },
    ],
  },

  // ─── Virtual Warehouse ───────────────────────────────────────────
  {
    key: 'virtual-warehouse',
    label: 'Virtual Warehouse',
    path: '/virtual-warehouse',
    icon: NavWarehouseIcon,
    permKey: 'virtualWarehouse',
    // Access frmVirtualFactoryMain has exactly 2 sub-nav (Received, Quality Discounts);
    // DN/RN print are toolbar buttons inside the Received tab, not standalone pages.
    children: [
      { key: 'vw-received', label: 'Received', path: '/virtual-warehouse/received', icon: Inventory },
      { key: 'quality-discount', label: 'Quality Discount', path: '/virtual-warehouse/quality-discount', icon: Science },
    ],
  },


  // ─── Master Data ─────────────────────────────────────────────────
  // Same 3-level sidebar shape as `Sales > Contract / Control > tabs`.
  // 1:1 with Access `frmMdMain` (cbMDList) + sub-form nav buttons:
  //   - Futures Control → frmMdMainModFuture (Calendar / Month / Holiday)
  //   - Item & Valuation → frmMdMainModItems (CP List / Items and Valuation / Other List)
  // Deepest level (Vendor / CP Bank / License / ... etc., from each
  // sub-form's cbPreferencesList) stays inside the page as a "Go to Page"
  // Select dropdown.
  {
    // Sub-navigation removed 2026-07-02: Master Data now opens a card-hub
    // landing at `/master-data` (style của `/<code>/data`); deeper navigation
    // tới Futures Control / Item & Valuation leaves đi qua các card trong hub,
    // không còn expand trên sidebar.
    key: 'master-data',
    label: 'Master Data',
    path: '/master-data',
    icon: NavMasterDataIcon,
    permKey: 'masterData',
  },

  // ─── Settings ────────────────────────────────────────────────────
  {
    key: 'settings',
    label: 'Settings',
    path: '/settings',
    icon: NavSettingsIcon,
    permKey: 'setting',
    children: [
      { key: 'roles', label: 'Roles', path: '/settings/roles', icon: ManageAccounts },
      { key: 'users', label: 'Users', path: '/settings/users', icon: Person },
      { key: 'log-history', label: 'Log History', path: '/settings/log-history', icon: ContentPasteSearch },
    ],
  },
]

/**
 * Flatten toàn bộ nav tree thành mảng leaf paths (cho route generation).
 */
export function flattenNavItems(items: NavItem[]): NavItem[] {
  const result: NavItem[] = []
  for (const item of items) {
    if (item.children && item.children.length > 0) {
      result.push(...flattenNavItems(item.children))
    } else {
      result.push(item)
    }
  }
  return result
}
